import {
  getMealsDetails,
  handleSubmitData
} from '@app/utilities/create-meal-helper';
import api from '@service/DataService';
import { getCountries } from 'country-state-picker';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import Resizer from 'react-image-file-resizer';
import { useDispatch, useSelector } from 'react-redux';
import { getCreateLoading } from '../redux/selector';
import { create_meal, update_meal } from '../redux/reducer';

const useCreateMeal = (meal, meal_categories, tags, health_conditions, id) => {
  const isEdit = !window.location.pathname.includes('create');
  const [images, setImages] = useState(null);
  const [mealCategories, setMealCategories] = useState([]);
  const [mealTags, setMealTags] = useState([]);
  const [healthConditions, setHealthConditions] = useState([]);
  const [generatingImages, setGeneratingImages] = useState({});
  const loading = useSelector(getCreateLoading);
  const dispatch = useDispatch();

  const {
    ingredients_list,
    ingredientsArray,
    types,
    meal_tags,
    nutrientsArray,
    categories,
    health,
    product_groups,
    incompatible_medical_conditions,
    instructionStepsArray,
    countriesData
  } = getMealsDetails(meal);

  useEffect(() => {
    if (meal && isEdit) {
      setImages(meal?.image ? meal?.image : null);
    }
  }, [meal, isEdit]);

  useEffect(() => {
    const response = meal_categories.map((data) => {
      return {
        value: data._id,
        label: data.name
      };
    });
    setMealCategories(response);
  }, [meal_categories]);

  useEffect(() => {
    const simplifiedTags = tags.flatMap((item) =>
      item.tags.map((tag) => ({
        value: tag._id,
        label: tag.name
      }))
    );
    setMealTags(simplifiedTags);
  }, [tags]);

  useEffect(() => {
    const response = health_conditions.map((data) => {
      return {
        value: data._id,
        label: data.name
      };
    });
    setHealthConditions(response);
  }, [health_conditions]);

  const formik = useFormik({
    initialValues: {
      name: meal?.name || '',
      video_url: meal?.video_url || '',
      description: meal?.description || '',
      types: types || '',
      countries: countriesData?.length > 0 ? countriesData : ['Nigeria'],
      prep_time: meal?.prep_time || 0,
      ingredients: ingredientsArray?.length > 0 ? ingredientsArray : [{ value: '' }],
      categories: categories?.length > 0 ? categories : [],
      health_conditions: health?.length > 0 ? health : [{ value: '' }],
      notification_message: meal?.notification_message || '',
      lu_tips: meal?.lu_tips || '',
      fat: meal?.fat || 0,
      fiber: meal?.fiber || 0,
      carbohydrate: meal?.carbohydrate || 0,
      protein: meal?.protein || 0,
      servings: meal?.servings || 0,
      total_calories: meal?.total_calories || 0,
      calorie_per_serving: meal?.calorie_per_serving || 0,
      product_groups:
        product_groups?.length > 0 ? product_groups : [{ value: '', id: '' }],
      ingredients_list:
        ingredients_list?.length > 0
          ? ingredients_list
          : [
              {
                details: { id: '', name: '' },
                description: '',
                quantity: 0,
                unit: { id: '', name: '' }
              }
            ],
      tags: meal_tags?.length > 0 ? meal_tags : [],
      nutrients:
        meal?.nutrients?.length >= 1 ? nutrientsArray : [{ name: '', value: '' }],
      incompatible_medical_conditions:
        incompatible_medical_conditions?.length > 0
          ? incompatible_medical_conditions
          : [''],
      instruction_steps:
        instructionStepsArray?.length > 0
          ? instructionStepsArray
          : [
              {
                step: '',
                time_estimate: undefined,
                image_url: undefined,
                video_url: undefined
              }
            ]
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      const {
        stringArray,
        formattedInstructionSteps,
        types,
        categories,
        health,
        incompatible_medical_conditions,
        tags,
        countries,
        ingredients_list,
        product_groups
      } = handleSubmitData(values);

      let data = {
        ...values,
        ingredients: stringArray,
        instruction_steps: formattedInstructionSteps,
        types: types,
        ingredients_list,
        countries,
        categories: categories,
        health_conditions: health,
        product_groups,
        incompatible_medical_conditions: incompatible_medical_conditions,
        images,
        tags
      };

      if (!data.video_url) delete data.video_url;
      if (!data.notification_message) delete data.notification_message;
      if (!data.lu_tips) delete data.lu_tips;
      if (!data.health_conditions?.length) delete data.health_conditions;
      if (!data.categories?.length) delete data.categories;

      if (id) {
        dispatch(update_meal({ result: data, id }));
      } else {
        dispatch(create_meal(data));
      }
    }
  });

  const countries = useMemo(() => {
    const countries = getCountries();
    return countries.map((data) => ({
      value: data.name,
      label: data.name
    }));
  }, []);

  const handlePickImage = (files) => {
    const file = files[0];
    Resizer.imageFileResizer(
      file,
      400,
      500,
      'JPEG',
      900,
      0,
      (uri) => {
        setImages(uri);
      },
      'file'
    );
  };

  const removeImage = () => {
    setImages(null);
  };

  const generateStepImage = async (idx) => {
    const stepText = formik.values?.instruction_steps[idx]?.step;
    if (!stepText) return;
    if (stepText?.length < 10) {
      toast.error('Instruction length must be at least 10 characters long');
      return;
    }
    setGeneratingImages((p) => ({ ...p, [idx]: true }));
    try {
      const payload = {
        instructions: stepText
      };
      const res = await api.post(`/cooking_step_images/generate`, payload);
      const req = res.data;
      const image = req.data.image_url;
      let steps = [...formik.values.instruction_steps];
      steps[idx] = {
        ...steps[idx],
        image_url: image
      };
      formik.setFieldValue('instruction_steps', steps);
    } catch (error) {
      console.log(error);
    } finally {
      setGeneratingImages((p) => ({ ...p, [idx]: false }));
    }
  };

  return {
    handleSubmit: formik.handleSubmit,
    setFieldValue: formik.setFieldValue,
    handleChange: formik.handleChange,
    values: formik.values,
    images,
    countries,
    mealCategories,
    health: healthConditions,
    mealTags,
    handlePickImage,
    removeImage,
    isEdit,
    generatingImages,
    generateStepImage,
    loading
  };
};

export default useCreateMeal;
